import { useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../hooks/useAuth';
import { useBrandingContext } from '../contexts/BrandingContext';
import { returnPathFrom } from '../utils/returnPath';
import { LoadingButton, InlineError, LanguageSwitcher } from '../components/ui';

export default function LoginPage() {
  const { t, i18n } = useTranslation();
  const { signIn } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const { schoolName, schoolNameLocalized, logoUrl, primaryColor } = useBrandingContext();

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      const { claims } = await signIn(email, password);
      // Redirect based on role (supports both role and roles[] fields)
      const hasAdminRole = claims?.role === 'admin' || claims?.role === 'superadmin' || claims?.roles?.includes('admin') || claims?.roles?.includes('superadmin');
      const hasTeacherRole = claims?.role === 'teacher' || claims?.roles?.includes('teacher');

      // Пряка връзка (не началната страница) към родителски екран — за всеки акаунт със семейство:
      // смесен админ/учител-родител идва от писмо до РОДИТЕЛЯ. Чист админ/учител — към своя панел.
      const back = returnPathFrom(location.state);
      // Check localStorage for last active role (for multi-role users)
      const lastActiveRole = localStorage.getItem('activeRole');
      if (back && back !== '/' && claims?.familyId) {
        navigate(back);
      } else if (lastActiveRole === 'admin' && hasAdminRole) {
        navigate('/admin');
      } else if (lastActiveRole === 'teacher' && hasTeacherRole) {
        navigate('/teacher');
      } else if (hasAdminRole) {
        navigate('/admin');
      } else if (hasTeacherRole) {
        navigate('/teacher');
      } else {
        // Родител: към поискания адрес (напр. пряката връзка към формата за подпис), иначе таблото.
        navigate(back ?? '/');
      }
    } catch (err: unknown) {
      console.error('Login error:', err);
      setError(t('auth.errors.generic'));
    } finally {
      setLoading(false);
    }
  };

  const displayName = schoolNameLocalized?.[i18n.language] || schoolName || t('app.title');

  return (
    <div className="min-h-screen bg-gradient-to-br from-primary-50 to-primary-100 flex items-center justify-center p-4">
      <div className="max-w-md w-full">
        <LanguageSwitcher variant="auth" activeColor={primaryColor} className="mb-6" />

        {/* Login Card */}
        <div className="bg-white rounded-2xl shadow-xl p-8">
          <div className="text-center mb-8">
            {/* Logo */}
            {logoUrl && (
              <img
                src={logoUrl}
                alt={displayName}
                className="h-20 w-auto mx-auto mb-4 object-contain"
              />
            )}
            {/* School Name — responsive, prevents overflow */}
            <h1
              className="text-xl sm:text-2xl font-bold leading-tight"
              style={{ color: primaryColor }}
            >
              {displayName}
            </h1>
            <p className="text-gray-500 mt-2 text-sm">{t('app.subtitle')}</p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="label">{t('auth.email')}</label>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="input"
                required
                disabled={loading}
                autoComplete="email"
              />
            </div>

            <div>
              <div className="flex justify-between items-center mb-1">
                <label className="label">{t('auth.password')}</label>
                <button
                  type="button"
                  onClick={() => navigate('/reset-password')}
                  className="text-xs hover:underline"
                  style={{ color: primaryColor }}
                >
                  {t('auth.forgotPassword', 'Forgot password?')}
                </button>
              </div>
              <input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="input"
                required
                disabled={loading}
                autoComplete="current-password"
              />
            </div>

            <InlineError message={error} onDismiss={() => setError('')} />

            <LoadingButton
              type="submit"
              loading={loading}
              loadingText={t('auth.loggingIn')}
              className="w-full btn btn-primary"
            >
              {t('auth.loginButton')}
            </LoadingButton>
          </form>

          <div className="mt-6 text-center text-sm text-gray-500">
            <p>Kühnplatz, 1040 Wien</p>
            <p className="mt-1">abvschule@gmail.com</p>
          </div>
        </div>

        <p className="text-center mt-6 text-sm text-gray-500">
          {displayName} © {new Date().getFullYear()}
        </p>
      </div>
    </div>
  );
}
